import { Navigate } from 'react-router';
import type { RouteObject } from 'react-router';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { usePageTitle } from '@/hooks/usePageTitle';
import { LoginPage } from '@/features/auth/components/LoginPage';
import { RegisterPage } from '@/features/auth/components/RegisterPage';
import { LeadsPage } from '@/features/leads/components';
import { LeadSearchPage } from '@/features/leads/pages/LeadSearchPage';
import { NewLeadPage } from '@/features/leads/pages/NewLeadPage';
import { JobsPage } from '@/features/jobs';
import { JobDetailPage } from '@/features/jobs/pages/JobDetailPage';
import { JobCostPage } from '@/features/jobs/pages/JobCostPage';
import { UsersPage } from '@/features/users';
import { SubcontractorsPage } from '@/features/subcontractors';
import { EstimatorAccountsPage } from '@/features/commissions/pages/EstimatorAccountsPage';
import { MyAccountPage } from '@/features/commissions/pages/MyAccountPage';
import { ReportsPage } from '@/features/reports';
import { LookupsPage, SettingsPage } from '@/features/admin';

// Placeholder components (will be replaced with actual feature components)
function Dashboard() {
  usePageTitle('Dashboard');
  return (
    <div className="container mx-auto p-6">
      <h2 className="text-2xl font-bold">Dashboard</h2>
      <p className="mt-4 text-muted-foreground">Welcome to Atlas Restoration</p>
    </div>
  );
}

function PaymentsPlaceholder() {
  usePageTitle('Payments');
  return <div>Payments (coming soon)</div>;
}

function SettingsPlaceholder() {
  usePageTitle('Settings');
  return <div>Settings (coming soon)</div>;
}

/**
 * Auth gate that redirects unauthenticated users to /login.
 * Renders children without any layout chrome.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" label="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Protected route wrapper that requires authentication and renders
 * the standard application layout (header navigation).
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <AppLayout>{children}</AppLayout>
    </RequireAuth>
  );
}

/**
 * Route definitions for the application
 */
export const routes: RouteObject[] = [
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/register',
    element: <RegisterPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <LeadsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: '/leads',
    element: (
      <ProtectedRoute>
        <LeadsPage />
      </ProtectedRoute>
    ),
  },
  {
    // Chrome-less focused New Lead route: authenticated, but outside
    // AppLayout (same precedent as /login and /register).
    path: '/leads/new',
    element: (
      <RequireAuth>
        <NewLeadPage />
      </RequireAuth>
    ),
  },
  {
    path: '/leads/:id',
    element: (
      <ProtectedRoute>
        <LeadsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/leads/search',
    element: (
      <ProtectedRoute>
        <LeadSearchPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/jobs',
    element: (
      <ProtectedRoute>
        <JobsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/jobs/:id',
    element: (
      <ProtectedRoute>
        <JobDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/jobs/:id/cost',
    element: (
      <ProtectedRoute>
        <JobCostPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/payments',
    element: (
      <ProtectedRoute>
        <PaymentsPlaceholder />
      </ProtectedRoute>
    ),
  },
  {
    path: '/reports',
    element: (
      <ProtectedRoute>
        <ReportsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/estimator-accounts',
    element: (
      <ProtectedRoute>
        <EstimatorAccountsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/users',
    element: (
      <ProtectedRoute>
        <UsersPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/subcontractors',
    element: (
      <ProtectedRoute>
        <SubcontractorsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <SettingsPlaceholder />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/lookups',
    element: (
      <ProtectedRoute>
        <LookupsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings',
    element: (
      <ProtectedRoute>
        <SettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/account',
    element: (
      <ProtectedRoute>
        <MyAccountPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];
